import { useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { anyApi } from "convex/server";
import { useProperty } from "../lib/property-context";
import { useImpersonatedUnitId } from "../lib/view-as-context";
import { QaThreadDialog } from "./QaThreadDialog";
import { inputCls, textareaCls } from "./form-controls";
import { UnitScopeField, type UnitScope } from "./UnitScopeField";

interface ThreadSummary {
  _id: string;
  title: string;
  body: string;
  isResolved: boolean;
  createdAt: number;
  replyCount: number;
  lastActivityAt: number;
  isMine: boolean;
  unitId: string | null;
  unitLabel: string | null;
  asker: {
    firstName: string | null;
    lastName: string | null;
    email: string;
  } | null;
}

// Tenant Q&A — open threads first, resolved threads collapsed below.
// Anyone with property membership can ask + reply; asker or landlord
// can mark a thread resolved. Visible to all members of the property.
export function QandASection() {
  const property = useProperty();
  const threads = useQuery(
    anyApi.qa.listForProperty,
    property ? { propertySlug: property.slug } : "skip",
  ) as ThreadSummary[] | null | undefined;
  const ask = useMutation(anyApi.qa.askQuestion);

  const [asking, setAsking] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [scope, setScope] = useState<UnitScope>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);

  const handleAsk = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!property) return;
    setSubmitting(true);
    setError(null);
    try {
      await ask({
        propertySlug: property.slug,
        title,
        body,
        unitId: scope || undefined,
      });
      setTitle("");
      setBody("");
      setScope(undefined);
      setAsking(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Landlord-as-tenant impersonation: filter to threads scoped to
  // the impersonated unit (plus property-wide). No-op otherwise.
  const impersonatedUnitId = useImpersonatedUnitId();
  const visibleThreads = useMemo(() => {
    if (!threads || impersonatedUnitId === null) return threads ?? [];
    return threads.filter(
      (t) => !t.unitId || t.unitId === impersonatedUnitId,
    );
  }, [threads, impersonatedUnitId]);

  const open = visibleThreads.filter((t) => !t.isResolved);
  const resolved = visibleThreads.filter((t) => t.isResolved);

  return (
    <>
      <section className="rounded-xl border border-slate-200 bg-white">
        <header className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              Q&amp;A
            </h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Ask a question or chime in on someone else's. The landlord can
              promote a useful answer to the FAQ.
            </p>
          </div>
          {!asking && (
            <button
              type="button"
              onClick={() => setAsking(true)}
              className="shrink-0 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 transition-colors"
            >
              Ask a question
            </button>
          )}
        </header>

        {asking && (
          <form
            onSubmit={handleAsk}
            className="px-6 py-4 border-b border-slate-100 space-y-3"
          >
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Question
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                autoFocus
                placeholder="e.g. Where do I find the spare key?"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Details
              </label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                required
                rows={3}
                placeholder="A bit more context — when, where, what you've tried."
                className={`${textareaCls} resize-none`}
              />
            </div>
            <UnitScopeField value={scope} onChange={setScope} />
            {error && <p className="text-xs text-red-600">{error}</p>}
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setAsking(false);
                  setTitle("");
                  setBody("");
                  setScope(undefined);
                  setError(null);
                }}
                disabled={submitting}
                className="text-xs font-medium text-slate-600 hover:text-slate-900 px-2 py-1 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !title.trim() || !body.trim()}
                className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {submitting ? "Posting…" : "Post question"}
              </button>
            </div>
          </form>
        )}

        <Body
          loading={threads === undefined}
          open={open}
          resolved={resolved}
          showResolved={showResolved}
          onToggleResolved={() => setShowResolved((v) => !v)}
          onOpenThread={(id) => setOpenThreadId(id)}
        />
      </section>

      <QaThreadDialog
        open={openThreadId !== null}
        onClose={() => setOpenThreadId(null)}
        threadId={openThreadId}
      />
    </>
  );
}

function Body({
  loading,
  open,
  resolved,
  showResolved,
  onToggleResolved,
  onOpenThread,
}: {
  loading: boolean;
  open: ThreadSummary[];
  resolved: ThreadSummary[];
  showResolved: boolean;
  onToggleResolved: () => void;
  onOpenThread: (id: string) => void;
}) {
  if (loading) {
    return <p className="px-6 py-4 text-sm text-slate-500">Loading…</p>;
  }
  if (open.length === 0 && resolved.length === 0) {
    return (
      <div className="px-6 py-8 text-center">
        <p className="text-sm font-medium text-slate-900">No questions yet.</p>
        <p className="mt-1 text-sm text-slate-500">
          Be the first — anything property-related is fair game.
        </p>
      </div>
    );
  }
  return (
    <div className="px-6 py-4 space-y-4">
      {open.length > 0 && (
        <Group title="Open">
          {open.map((t) => (
            <ThreadRow
              key={t._id}
              thread={t}
              onClick={() => onOpenThread(t._id)}
            />
          ))}
        </Group>
      )}
      {resolved.length > 0 && (
        <div>
          <button
            type="button"
            onClick={onToggleResolved}
            className="text-[10px] font-medium uppercase tracking-[0.2em] text-slate-400 hover:text-slate-600 transition-colors"
          >
            {showResolved
              ? `Hide resolved (${resolved.length})`
              : `Show resolved (${resolved.length})`}
          </button>
          {showResolved && (
            <ul className="mt-3 divide-y divide-slate-100">
              {resolved.map((t) => (
                <ThreadRow
                  key={t._id}
                  thread={t}
                  onClick={() => onOpenThread(t._id)}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Group({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="text-[10px] font-medium uppercase tracking-[0.2em] text-slate-400">
        {title}
      </h3>
      <ul className="mt-3 divide-y divide-slate-100">{children}</ul>
    </div>
  );
}

function ThreadRow({
  thread,
  onClick,
}: {
  thread: ThreadSummary;
  onClick: () => void;
}) {
  return (
    <li
      onClick={onClick}
      className="cursor-pointer py-3 first:pt-0 -mx-2 px-2 rounded-md hover:bg-slate-50/50 transition-colors"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <p className="text-sm font-medium text-slate-900">{thread.title}</p>
          {thread.unitLabel && (
            <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-600">
              {thread.unitLabel}
            </span>
          )}
        </div>
        <span className="shrink-0 text-xs text-slate-500">
          {thread.replyCount === 0
            ? "no replies"
            : thread.replyCount === 1
              ? "1 reply"
              : `${thread.replyCount} replies`}
        </span>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        {thread.isMine ? "You" : authorName(thread.asker)} ·{" "}
        {timeAgo(thread.lastActivityAt)}
      </p>
    </li>
  );
}

function authorName(a: ThreadSummary["asker"]): string {
  if (!a) return "Unknown";
  if (a.firstName) {
    return a.lastName ? `${a.firstName} ${a.lastName.charAt(0)}.` : a.firstName;
  }
  return a.email.split("@")[0];
}

function timeAgo(ts: number): string {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}
